import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useUser } from '../context/UserContext';
import { Lock, Trophy, Star, Award, Medal, Crown, Gem, Target, Zap, Flame } from 'lucide-react';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  requirement_value: number;
  points: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  is_hidden: boolean;
  unlocked: boolean;
  unlock_count: number;
  unlocked_at?: string;
  progress?: number;
}

const iconMap: Record<string, any> = {
  trophy: Trophy,
  star: Star,
  award: Award,
  medal: Medal,
  crown: Crown,
  gem: Gem,
  target: Target,
  zap: Zap,
  flame: Flame
};

export const Achievements: React.FC = () => {
  const { user } = useUser();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [totalPoints, setTotalPoints] = useState(0);

  useEffect(() => {
    if (user) {
      loadAchievements();
    }
  }, [user]);

  const loadAchievements = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data: allAchievements, error: achievementsError } = await supabase
        .from('achievements')
        .select('*')
        .is('deleted_at', null)
        .order('sort_order', { ascending: true });

      if (achievementsError) throw achievementsError;

      const { data: userAchievements, error: userError } = await supabase
        .from('user_achievements')
        .select('achievement_id, unlocked_at, progress')
        .eq('user_id', user.id);

      if (userError) throw userError;

      const achievementsWithStatus = await Promise.all(
        (allAchievements || []).map(async (achievement) => {
          const userAchievement = userAchievements?.find(
            (ua) => ua.achievement_id === achievement.id
          );

          const { count } = await supabase
            .from('user_achievements')
            .select('*', { count: 'exact', head: true })
            .eq('achievement_id', achievement.id);

          return {
            ...achievement,
            unlocked: !!userAchievement,
            unlocked_at: userAchievement?.unlocked_at,
            progress: userAchievement?.progress || 0,
            unlock_count: count || 0
          };
        })
      );

      setAchievements(achievementsWithStatus);

      const points = achievementsWithStatus
        .filter((a) => a.unlocked)
        .reduce((sum, a) => sum + a.points, 0);
      setTotalPoints(points);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity: string) => {
    return 'from-kod-purple-900 to-kod-purple-900';
  };

  const getRarityTextColor = (rarity: string) => {
    return 'text-white';
  };

  const getRarityBorderColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'border-orange-500';
      case 'rare': return 'border-orange-400';
      case 'epic': return 'border-orange-500';
      case 'legendary': return 'border-yellow-400';
      default: return 'border-orange-500';
    }
  };

  const getIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || Trophy;
    return IconComponent;
  };

  const filteredAchievements = achievements.filter((achievement) => {
    if (filter === 'all') return true;
    if (filter === 'unlocked') return achievement.unlocked;
    if (filter === 'locked') return !achievement.unlocked;
    return achievement.category === filter;
  });

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const totalCount = achievements.length;
  const completionPercentage = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <img
            src="/TSL_Logo.png"
            alt="Logo"
            className="w-[600px] h-[600px] object-contain"
          />
        </div>
        <div className="relative z-10 animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Achievements</h1>
        <p className="text-white/70 mb-6">Track your progress and unlock rewards</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-kod-purple-700 to-kod-purple-800 rounded-xl p-6 border border-kod-purple-600/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Unlocked</p>
                <p className="text-3xl font-bold">{unlockedCount}/{totalCount}</p>
                <p className="text-white/60 text-sm mt-1">{completionPercentage}% Complete</p>
              </div>
              <Trophy size={48} className="text-kod-accent opacity-50" />
            </div>
            <div className="mt-4 bg-kod-purple-900/50 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-kod-accent to-yellow-500 h-full transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-700/20 rounded-xl p-6 border border-yellow-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Total Points</p>
                <p className="text-3xl font-bold text-yellow-400">{totalPoints}</p>
                <p className="text-white/60 text-sm mt-1">Achievement Points</p>
              </div>
              <Star size={48} className="text-yellow-400 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-kod-accent/20 to-kod-accent-dark/20 rounded-xl p-6 border border-kod-accent/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Rarest Achievement</p>
                <p className="text-xl font-bold truncate">
                  {achievements.find(a => a.unlocked && a.rarity === 'legendary')?.title ||
                   achievements.find(a => a.unlocked && a.rarity === 'epic')?.title ||
                   achievements.find(a => a.unlocked && a.rarity === 'rare')?.title ||
                   'None yet'}
                </p>
                <p className="text-white/60 text-sm mt-1">Keep grinding!</p>
              </div>
              <Crown size={48} className="text-kod-accent opacity-50" />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            All ({totalCount})
          </button>
          <button
            onClick={() => setFilter('unlocked')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'unlocked'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Unlocked ({unlockedCount})
          </button>
          <button
            onClick={() => setFilter('locked')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'locked'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Locked ({totalCount - unlockedCount})
          </button>
          <button
            onClick={() => setFilter('recording')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'recording'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Recording
          </button>
          <button
            onClick={() => setFilter('tournament')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'tournament'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Tournament
          </button>
          <button
            onClick={() => setFilter('skill')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'skill'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Skill
          </button>
          <button
            onClick={() => setFilter('social')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'social'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Social
          </button>
          <button
            onClick={() => setFilter('milestone')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'milestone'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            Milestone
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredAchievements.map((achievement) => {
          const IconComponent = getIcon(achievement.icon);
          const isLocked = !achievement.unlocked && achievement.is_hidden;

          return (
            <div
              key={achievement.id}
              className={`relative rounded-xl p-6 border-2 transition-all duration-300 hover:scale-105 ${
                achievement.unlocked
                  ? `bg-gradient-to-br ${getRarityColor(achievement.rarity)} ${getRarityBorderColor(achievement.rarity)} shadow-lg shadow-orange-500/20`
                  : 'bg-kod-purple-800/50 border-kod-purple-600/30 grayscale'
              }`}
            >
              {achievement.unlocked && (
                <div className="absolute top-3 right-3">
                  <img
                    src="/TSL_Logo.png"
                    alt="TSL Official"
                    className="w-12 h-12 object-contain drop-shadow-lg"
                  />
                </div>
              )}

              <div className="flex flex-col items-center text-center">
                <div
                  className={`relative w-20 h-20 rounded-full flex items-center justify-center mb-4 border-2 ${
                    achievement.unlocked ? 'bg-kod-purple-900 border-orange-500' : 'bg-kod-purple-700/50 border-kod-purple-600'
                  }`}
                >
                  {isLocked ? (
                    <Lock size={36} className="text-white/40" />
                  ) : (
                    <IconComponent
                      size={36}
                      className={achievement.unlocked ? 'text-white' : 'text-white/40'}
                    />
                  )}
                </div>

                <h3 className={`font-bold text-lg mb-2 ${achievement.unlocked ? 'text-white' : 'text-white/60'}`}>
                  {isLocked ? '???' : achievement.title}
                </h3>

                <p className={`text-sm mb-4 ${achievement.unlocked ? 'text-white/90' : 'text-white/50'}`}>
                  {isLocked ? 'Complete the requirements to unlock' : achievement.description}
                </p>

                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      achievement.unlocked ? 'text-white bg-orange-500/20 border-orange-500' : 'text-white/40 bg-kod-purple-700/50 border-kod-purple-600'
                    }`}
                  >
                    {achievement.rarity}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      achievement.unlocked ? 'text-white bg-orange-500/20 border-orange-500' : 'text-white/40 bg-kod-purple-700/50 border-kod-purple-600'
                    }`}
                  >
                    {achievement.points} pts
                  </span>
                </div>

                <div className="text-xs text-white/50 mt-2">
                  {achievement.unlock_count} {achievement.unlock_count === 1 ? 'player' : 'players'} unlocked
                </div>

                {achievement.unlocked && achievement.unlocked_at && (
                  <div className="text-xs text-white/60 mt-2">
                    Unlocked {new Date(achievement.unlocked_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredAchievements.length === 0 && (
        <div className="text-center py-12 text-white/50">
          <Trophy size={64} className="mx-auto mb-4 opacity-50" />
          <p className="text-xl">No achievements found</p>
          <p className="text-sm mt-2">Try a different filter</p>
        </div>
      )}
    </div>
  );
};
